import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExceptionType } from '@/types/asset-audit';
import type { AuditException } from '@/types/asset-audit';
import { CheckCircle, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ResolveExceptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exception: AuditException | null;
  onResolve: (data: ResolveExceptionData) => void;
}

export interface ResolveExceptionData {
  actionTaken: string;
  updateAssetRecord: boolean;
  newLocation?: string;
  createWorkOrder: boolean;
  attachedFiles: File[];
}

export function ResolveExceptionModal({
  open,
  onOpenChange,
  exception,
  onResolve,
}: Readonly<ResolveExceptionModalProps>) {
  const [formData, setFormData] = useState<ResolveExceptionData>({
    actionTaken: '',
    updateAssetRecord: false,
    newLocation: '',
    createWorkOrder: false,
    attachedFiles: [],
  });

  const [fileInputKey, setFileInputKey] = useState(0);

  React.useEffect(() => {
    if (exception) {
      setFormData({
        actionTaken: '',
        updateAssetRecord: false,
        newLocation: '',
        createWorkOrder: false,
        attachedFiles: [],
      });
      setFileInputKey(prev => prev + 1);
    }
  }, [exception]);

  const handleResolve = () => {
    if (!formData.actionTaken.trim()) {
      return;
    }
    onResolve(formData);
    onOpenChange(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData({
        ...formData,
        attachedFiles: Array.from(e.target.files),
      });
    }
  };

  if (!exception) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Resolve Exception
          </DialogTitle>
          <DialogDescription>
            Document the actions taken to resolve this exception.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Exception Info */}
          <div className="rounded-lg border bg-gray-50 p-4 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={getSeverityColor(exception.severity)}>
                {exception.severity}
              </Badge>
              <span className="font-medium">{exception.exceptionType}</span>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <div>
                <span className="font-medium">Asset:</span> {exception.assetCode} - {exception.assetName}
              </div>
              <div>
                <span className="font-medium">Description:</span> {exception.description}
              </div>
              {exception.expectedValue && exception.actualValue && (
                <div className="mt-2 pt-2 border-t">
                  <div className="text-gray-500 line-through">
                    Expected: {exception.expectedValue}
                  </div>
                  <div className="font-semibold text-gray-900">
                    Actual: {exception.actualValue}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Taken (Required) */}
          <div className="space-y-2">
            <Label htmlFor="actionTaken" className="flex items-center gap-1">
              Action Taken <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="actionTaken"
              value={formData.actionTaken}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, actionTaken: e.target.value })}
              placeholder="Describe the actions taken to resolve this exception..."
              rows={4}
              className="resize-none"
              required
            />
            <p className="text-xs text-gray-500">
              Provide details about how this exception was resolved.
            </p>
          </div>

          {/* Update Asset Record */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="updateAsset"
                checked={formData.updateAssetRecord}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, updateAssetRecord: checked as boolean })
                }
              />
              <Label
                htmlFor="updateAsset"
                className="text-sm font-normal cursor-pointer"
              >
                Update asset record with corrected information
              </Label>
            </div>

            {/* New Location (conditional) */}
            {formData.updateAssetRecord && exception.exceptionType === ExceptionType.LocationMismatch && (
              <div className="ml-6 space-y-2">
                <Label htmlFor="newLocation">New Location</Label>
                <Select
                  value={formData.newLocation}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, newLocation: value })
                  }
                >
                  <SelectTrigger id="newLocation">
                    <SelectValue placeholder="Select new location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="building-a-floor-1">Building A - Floor 1</SelectItem>
                    <SelectItem value="building-a-floor-2">Building A - Floor 2</SelectItem>
                    <SelectItem value="building-b-floor-1">Building B - Floor 1</SelectItem>
                    <SelectItem value="building-b-floor-2">Building B - Floor 2</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="storage">Storage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Create Work Order */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="createWorkOrder"
              checked={formData.createWorkOrder}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, createWorkOrder: checked as boolean })
              }
            />
            <Label
              htmlFor="createWorkOrder"
              className="text-sm font-normal cursor-pointer"
            >
              Create work order for follow-up maintenance
            </Label>
          </div>

          {/* Attach Files */}
          <div className="space-y-2">
            <Label htmlFor="attachFiles">Attach Files (Optional)</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('fileInput')?.click()}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                {formData.attachedFiles.length > 0
                  ? `${formData.attachedFiles.length} file(s) selected`
                  : 'Choose files'}
              </Button>
              <input
                key={fileInputKey}
                id="fileInput"
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx"
              />
            </div>
            {formData.attachedFiles.length > 0 && (
              <div className="text-xs text-gray-500 space-y-1">
                {formData.attachedFiles.map((file) => (
                  <div key={`file-${file.name}-${file.size}`}>• {file.name}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            disabled={!formData.actionTaken.trim()}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Resolve Exception
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
